import { useEffect, useRef, useState } from "react";

import {
  evaluateScanCooldown,
  type ScanCooldownState,
} from "../../../../packages/guard-core/src/scan-cooldown.ts";
import { captureLocation, type GeolocationLike } from "./geolocation";
import { NfcScanner } from "./NfcScanner";
import { resolveScannedTag } from "./nfc-scan";
import type { QueueStore } from "./offline-queue";
import { QrScanner } from "./QrScanner";
import { resolveScannedToken } from "./qr-scan";
import { captureReceipt, scanReceiptToneRole, type ScanReceipt } from "./receipt";
import { buildManualScanItem, buildScanItem, submitScan, type ScanSyncRpcClient } from "./submit-scan";
import { locationChip, useLocationStatus, type PermissionsLike } from "./use-location-status";
import { defaultIsOnline, useOfflineQueue } from "./use-offline-queue";
import type { ScanMethod } from "./scan-contract";
import { NOOP_ANALYTICS, type Analytics } from "../lib/analytics";
import type { CapturedMark } from "../duty/duty-progress";
import type { DeviceContext } from "../lib/device";

// A checkpoint the guard can capture manually, chosen from their route's list. The id is a
// server-resolvable checkpoint UUID — guards never type the opaque chk_ token (QR carries that).
export interface CheckpointOption {
  checkpointId: string;
  label: string;
}

// Capture screen — Guard Web checkpoint capture.
//
// PRIMARY: live QR scan (BarcodeDetector + camera) where the device supports it. FALLBACK: a
// checkpoint picker that submits checkpointId + empty token. Both routes share runCapture, which is
// OFFLINE-FIRST: offline (or failed-submit) captures land in the durable offline queue and replay
// FIFO with the same idempotency key on reconnect/app-load/manual retry — a scan is never lost to a
// dead signal. Receipts reuse the shared guard-core copy ("Saved offline", "Saved — will retry").
// The queue pill surfaces saved/syncing/synced/rejected counts; labels only, never tokens.

/** Per-method web gates (Nick's everything-behind-a-flag rule). Device capability still applies. */
export interface CaptureFeatureFlags {
  qrCapture: boolean;
  nfcCapture: boolean;
  manualCapture: boolean;
}

const ALL_CAPTURE_FLAGS_ON: CaptureFeatureFlags = {
  qrCapture: true,
  nfcCapture: true,
  manualCapture: true,
};

export function CaptureScreen({
  client,
  accountId,
  device,
  deviceId,
  checkpoints = [],
  routeAssignmentId = null,
  featureFlags = ALL_CAPTURE_FLAGS_ON,
  analytics = NOOP_ANALYTICS,
  onCaptured,
  queueStore,
  isOnline = defaultIsOnline,
  now = Date.now,
  geolocation,
  permissions,
}: {
  client: ScanSyncRpcClient;
  accountId: string;
  device: DeviceContext;
  deviceId: string;
  checkpoints?: readonly CheckpointOption[];
  routeAssignmentId?: string | null;
  /** Tenant/user method gates; defaults to everything on (server resolver may say otherwise). */
  featureFlags?: CaptureFeatureFlags;
  /** Allowlist-only analytics; defaults to the no-op (no key/flag = nothing leaves the device). */
  analytics?: Analytics;
  /** Duty-home seam: fired after every accepted capture with whatever identity is known. */
  onCaptured?: (mark: CapturedMark) => void;
  /** Test seam; defaults to the localStorage-backed store. */
  queueStore?: QueueStore;
  /** Test seam; defaults to navigator.onLine. */
  isOnline?: () => boolean;
  /** Clock seam for the scan cooldown; defaults to Date.now. */
  now?: () => number;
  /** Test seam; defaults to navigator.geolocation. */
  geolocation?: GeolocationLike;
  /** Test seam; defaults to navigator.permissions (null = API absent). */
  permissions?: PermissionsLike | null;
}) {
  const [selectedId, setSelectedId] = useState("");
  const [scanning, setScanning] = useState(false);
  const [nfcScanning, setNfcScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<ScanReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  // GW-PARITY-2B: guard-core scan cooldown — UX pacing for rapid same-checkpoint repeats.
  // Server-side duplicate/idempotency behavior is untouched; this only calms the experience.
  const [pacing, setPacing] = useState<string | null>(null);
  const cooldownRef = useRef<ScanCooldownState | null>(null);
  // GW-PARITY-2C: anchor a fresh receipt into view — the officer always sees the result card.
  const receiptRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (receipt !== null) {
      receiptRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    }
  }, [receipt]);

  const queue = useOfflineQueue({
    submit: (item) => submitScan(client, accountId, item),
    isOnline,
    ...(queueStore !== undefined ? { store: queueStore } : {}),
  });

  // GPS is visible duty readiness (like the native app), not just a capture side effect: the chip
  // lets a guard grant location up front so the first checkpoint capture doesn't stall on a prompt.
  const location = useLocationStatus({
    ...(geolocation !== undefined ? { geolocation } : {}),
    ...(permissions !== undefined ? { permissions } : {}),
  });
  const gpsChip = locationChip(location);

  // A camera is the ONLY hard device requirement for QR — the scanner uses the native
  // BarcodeDetector where it exists (Android/Chromium) and falls back to jsQR frame decoding on
  // iOS WebKit. Tenant/user flags then gate each method on top of device capability.
  const canScan = device.hasCamera && featureFlags.qrCapture;
  // Progressive Web NFC: Android Chrome only (NDEFReader). iOS/desktop never see the affordance.
  const canNfc = device.hasWebNfc && featureFlags.nfcCapture;
  const canManual = featureFlags.manualCapture;
  const manualAvailable = checkpoints.length > 0;

  async function runCapture(input: {
    method: ScanMethod;
    token: string;
    checkpointId?: string;
  }): Promise<void> {
    setError(null);
    setPacing(null);
    const cooldown = evaluateScanCooldown({
      rawValue: input.token !== "" ? input.token : input.checkpointId ?? "",
      nowMs: now(),
      previous: cooldownRef.current,
    });
    if (!cooldown.accepted) {
      const seconds = Math.max(1, Math.ceil(cooldown.remainingMs / 1000));
      setPacing(`That checkpoint was just captured — try again in ${seconds}s.`);
      return;
    }
    cooldownRef.current = cooldown.nextState;
    setReceipt(null);
    setBusy(true);
    const online = isOnline();
    const fix = await captureLocation({ geolocation });
    const captureBase = {
      lat: fix.lat,
      lng: fix.lng,
      gpsAccuracyMeters: fix.accuracyMeters,
      gpsPermission: fix.permission,
      deviceId,
      appVersion: __APP_VERSION__,
      routeAssignmentId,
      offlineCreated: !online,
    };
    const item =
      input.method === "MANUAL" && input.checkpointId !== undefined
        ? buildManualScanItem({ ...captureBase, checkpointId: input.checkpointId })
        : buildScanItem({
            token: input.token,
            scanMethod: input.method,
            ...captureBase,
            ...(input.checkpointId !== undefined ? { checkpointId: input.checkpointId } : {}),
          });

    if (!online) {
      // No network — straight to the durable queue; it replays on reconnect.
      queue.enqueue(item);
      setBusy(false);
      setReceipt(captureReceipt({ kind: "queued-offline" }, input.method));
      analytics.capture("capture_completed", { appVersion: __APP_VERSION__, method: input.method, offline: true, queued: true });
      onCaptured?.({ checkpointId: input.checkpointId ?? null, checkpointName: null });
      return;
    }

    const result = await submitScan(client, accountId, item);
    setBusy(false);
    if (result.ok) {
      setReceipt(captureReceipt({ kind: "outcome", outcome: result.outcome }, input.method));
      analytics.capture("capture_completed", { appVersion: __APP_VERSION__, method: input.method, offline: false, queued: false, outcome: result.outcome });
      if (result.outcome !== "rejected") {
        onCaptured?.({ checkpointId: input.checkpointId ?? null, checkpointName: result.checkpointName });
      }
    } else {
      // Couldn't reach the server — save it; the queue retries with the same idempotency key.
      queue.enqueue(item);
      setReceipt(captureReceipt({ kind: "queued-retry" }, input.method));
      analytics.capture("capture_completed", { appVersion: __APP_VERSION__, method: input.method, offline: false, queued: true });
      onCaptured?.({ checkpointId: input.checkpointId ?? null, checkpointName: null });
    }
  }

  function handleScannedToken(rawValue: string): void {
    setScanning(false);
    const resolved = resolveScannedToken(rawValue);
    if (!resolved.ok) {
      setReceipt(null);
      setError(resolved.reason);
      return;
    }
    void runCapture({ method: "QR", token: resolved.token });
  }

  function handleScannedTag(text: string | null): void {
    setNfcScanning(false);
    const resolved = resolveScannedTag(text);
    if (!resolved.ok) {
      setReceipt(null);
      setError(resolved.reason);
      return;
    }
    void runCapture({ method: "NFC", token: resolved.token });
  }

  function submitManual(): void {
    if (!manualAvailable) {
      return;
    }
    if (selectedId.length === 0) {
      setError("Select a checkpoint.");
      return;
    }
    void runCapture({ method: "MANUAL", token: "", checkpointId: selectedId });
  }

  return (
    <section className="capture" aria-labelledby="capture-title">
      <h2 id="capture-title" className="capture__title">
        Capture a checkpoint
      </h2>

      <div className="capture__status">
        {gpsChip.actionable ? (
          <button
            className={`status-chip status-chip--${gpsChip.tone}`}
            type="button"
            onClick={() => void location.requestLocation()}
          >
            {gpsChip.label}
          </button>
        ) : (
          <span className={`status-chip status-chip--${gpsChip.tone}`} role="status">
            {gpsChip.label}
          </span>
        )}
      </div>

      {scanning ? (
        <QrScanner
          onToken={handleScannedToken}
          onClose={() => setScanning(false)}
          onError={(message) => {
            setScanning(false);
            setError(message);
          }}
        />
      ) : nfcScanning ? (
        <NfcScanner
          onTag={handleScannedTag}
          onClose={() => setNfcScanning(false)}
          onError={(message) => {
            setNfcScanning(false);
            setError(message);
          }}
        />
      ) : null}

      {canManual ? (
      <div className="capture__manual">
        <h3 className="capture__manual-title">{canScan ? "Can't scan? Manual fallback" : "Manual fallback"}</h3>
        {manualAvailable ? (
          <>
            <label className="capture__field">
              <span>Checkpoint</span>
              <select
                name="checkpoint"
                value={selectedId}
                onChange={(event) => setSelectedId(event.target.value)}
                disabled={busy}
              >
                <option value="">Select a checkpoint…</option>
                {checkpoints.map((checkpoint) => (
                  <option key={checkpoint.checkpointId} value={checkpoint.checkpointId}>
                    {checkpoint.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="capture__submit"
              type="button"
              onClick={submitManual}
              disabled={busy}
            >
              {busy ? "Submitting…" : "Submit manual capture"}
            </button>
          </>
        ) : (
          <p className="capture__unavailable" role="status">
            Manual selection appears here once your route's checkpoints are loaded.
            {canScan ? " Or use the scan buttons below." : ""}
          </p>
        )}
      </div>
      ) : null}

      {pacing ? (
        <p className="capture__pacing" role="status">
          {pacing}
        </p>
      ) : null}

      {error ? (
        <p className="capture__error" role="alert">
          {error}
        </p>
      ) : null}

      {receipt ? (
        <div
          ref={receiptRef}
          className={`capture__receipt capture__receipt--${scanReceiptToneRole(receipt.tone)}`}
          role="status"
        >
          <p className="capture__receipt-title">{receipt.title}</p>
          <p className="capture__receipt-detail">{receipt.detail}</p>
        </div>
      ) : null}

      {queue.counts.total > 0 ? (
        <div className="capture__queue" role="status" aria-label="Offline scan queue">
          {queue.counts.queued > 0 ? (
            <span className="queue-pill queue-pill--warn">
              {queue.counts.queued} saved offline
            </span>
          ) : null}
          {queue.flushing || queue.counts.syncing > 0 ? (
            <span className="queue-pill queue-pill--info">syncing…</span>
          ) : null}
          {queue.counts.synced > 0 ? (
            <span className="queue-pill queue-pill--accent">{queue.counts.synced} synced</span>
          ) : null}
          {queue.counts.rejected > 0 ? (
            <span className="queue-pill queue-pill--error">{queue.counts.rejected} rejected</span>
          ) : null}
          {queue.counts.queued > 0 ? (
            <button
              className="capture__retry"
              type="button"
              onClick={() => void queue.flush()}
              disabled={queue.flushing}
            >
              Sync now
            </button>
          ) : null}
        </div>
      ) : null}

      {/* GW-PARITY-2C: sticky, safe-area-aware action dock — the primary capture action is always
          reachable without scroll-hunting. NFC is PRIMARY where the browser supports it; QR is the
          backup; on iOS-like browsers QR takes the primary slot. */}
      {!scanning && !nfcScanning ? (
        <div className="capture__dock">
          {canNfc ? (
            <button
              className="capture__dock-primary"
              type="button"
              onClick={() => {
                setError(null);
                setReceipt(null);
                setNfcScanning(true);
              }}
              disabled={busy}
            >
              Tap NFC tag
            </button>
          ) : null}
          {canScan ? (
            <button
              className={canNfc ? "capture__dock-secondary" : "capture__dock-primary"}
              type="button"
              onClick={() => {
                setError(null);
                setReceipt(null);
                setScanning(true);
              }}
              disabled={busy}
            >
              Scan QR code
            </button>
          ) : null}
          {!canScan && !canNfc ? (
            <p className="capture__hint">
              {canManual
                ? "Camera scanning isn't available on this device — use the manual fallback above, or scan with the native app."
                : "Capture on the web is turned off for your account — use the SecOps Guard app, or contact your supervisor."}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
